import React, { createContext, useContext, useReducer, useEffect } from 'react';

const AuthContext = createContext();

// Initial state
const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
};

// Reducer function
const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        loading: true,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    default:
      return state;
  }
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check if user is already logged in (from localStorage)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Verify token is still valid by making a request to get user profile
      const verifyToken = async () => {
        try {
          const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: {
                user: data.user,
                token: token,
              },
            });
          } else {
            // Token is invalid, remove it
            localStorage.removeItem('token');
            dispatch({ type: 'LOGIN_FAILURE' });
          }
        } catch (error) {
          console.error('Error verifying token:', error);
          localStorage.removeItem('token');
          dispatch({ type: 'LOGIN_FAILURE' });
        }
      };

      verifyToken();
    } else {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Login remains passwordless for the current auth model.
  const login = async (username) => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/sign-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier: username }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store token in localStorage
        localStorage.setItem('token', data.token);

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: data.user,
            token: data.token,
          },
        });

        return { success: true, message: 'Sign in successful' };
      } else {
        dispatch({ type: 'LOGIN_FAILURE' });
        return { success: false, message: data.message || 'Sign in failed' };
      }
    } catch (error) {
      console.error('Sign-in error:', error);
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, message: 'Network error occurred' };
    }
  };

  // Passwordless sign-in function
  const passwordlessSignIn = async (identifier) => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/sign-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ identifier }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store token in localStorage
        localStorage.setItem('token', data.token);

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: data.user,
            token: data.token,
          },
        });

        return { success: true, message: 'Sign in successful' };
      } else {
        dispatch({ type: 'LOGIN_FAILURE' });
        return { success: false, message: data.message || 'Sign in failed' };
      }
    } catch (error) {
      console.error('Passwordless sign-in error:', error);
      dispatch({ type: 'LOGIN_FAILURE' });
      return { success: false, message: 'Network error occurred' };
    }
  };

  // Logout function
  const logout = () => {
    // Remove token from localStorage
    localStorage.removeItem('token');
    
    dispatch({ type: 'LOGOUT' });
  };

  const register = async () => {
    return {
      success: false,
      message: 'Registration is disabled while passwordless auth is active.',
    };
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        register,
        passwordlessSignIn,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
